import { OrderStatus, OrderType } from "@/api/payments";
import { Button, Chip, Stack } from "@mui/material";
import { FC, useMemo } from "react";
import { useListContext } from "react-admin";
import { formatOrderStatus, formatOrderType } from "../../utils";

type ChipDescriptor = {
  id: string;
  label: string;
  onDelete: () => void;
};

const FiltersList: FC = () => {
  const { filterValues, setFilters } = useListContext();

  const statusChips = useMemo<ChipDescriptor[]>(() => {
    const current: OrderStatus[] = filterValues.statuses ?? [];
    return current.map((s) => ({
      id: `status:${s}`,
      label: formatOrderStatus(s),
      onDelete: () =>
        setFilters({
          ...filterValues,
          statuses: current.filter((v) => v !== s),
        }),
    }));
  }, [filterValues, setFilters]);

  const typeChips = useMemo<ChipDescriptor[]>(() => {
    const current: OrderType[] = filterValues.types ?? [];
    return current.map((t) => ({
      id: `type:${t}`,
      label: formatOrderType(t),
      onDelete: () =>
        setFilters({
          ...filterValues,
          types: current.filter((v) => v !== t),
        }),
    }));
  }, [filterValues, setFilters]);

  const allChips = [...statusChips, ...typeChips];

  const hasFilters =
    !!filterValues.statuses?.length || !!filterValues.types?.length;

  if (!hasFilters) return null;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" mt={1}>
      {allChips.map(({ id, label, onDelete }) => (
        <Chip
          key={id}
          size="small"
          variant="filled"
          label={label}
          onClick={onDelete}
          onDelete={onDelete}
        />
      ))}
      <Button
        size="small"
        variant="outlined"
        onClick={() =>
          setFilters({
            ...filterValues,
            statuses: undefined,
            types: undefined,
          })
        }
      >
        Clear All
      </Button>
    </Stack>
  );
};

export { FiltersList };
